import type {Weekday, WeekdayDescription} from '#lit/common/constants/dates.js';
import {WeekdaysEnum, getWeekdays} from '#lit/common/constants/dates.js';
import {ChangeWeekdayEvent, validateWeekdays} from './utils.js';
import type {
  Cmd,
  ImmutableProgram,
  ImmutableResult,
  InitialProperties,
} from '#lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
} from '#lit/components/mixins/immutable_component/index.js';
import type {VariantsFor} from '#lit/types/architecture.js';
import type WeekdaySelector from './index.js';
import {locale} from '#lit/common/locale/index.js';

export type Model = {
  locale: string;
  days: {
    all: WeekdayDescription[];
    weekend: WeekdayDescription[];
  };
  value: Set<Weekday>;
  valueAsArray: Weekday[];
  onlyWorkdays: boolean;
  multiSelection: boolean;
  disabled: boolean;
};

export const Msg = makeVariants({
  setLocale: Variant<string>()('set-locale'),
  setOnlyWorkdays: Variant<boolean>()('set-only-workdays'),
  setValue: Variant<Weekday[]>()('set-value'),
  setSingleValue: Variant<Weekday>()('set-single-value'),
  selectWeekday: Variant<Weekday>()('select-weekday'),
  deselectWeekday: Variant<Weekday>()('deselect-weekday'),
  allowMultiSelection: Variant<boolean>()('allow-multi-selection'),
  enable: Variant<void>()('enable'),
  disable: Variant<void>()('disable'),
});

export type Msg = VariantsFor<typeof Msg>;

// -- Init

function getSelectorWeekdays(locale: string): WeekdayDescription[] {
  const record = getWeekdays(locale);
  return [
    record[WeekdaysEnum.SUNDAY],
    record[WeekdaysEnum.MONDAY],
    record[WeekdaysEnum.TUESDAY],
    record[WeekdaysEnum.WEDNESDAY],
    record[WeekdaysEnum.THURSDAY],
    record[WeekdaysEnum.FRIDAY],
    record[WeekdaysEnum.SATURDAY],
  ];
}

function getSelectorWeekendDays(locale: string): WeekdayDescription[] {
  const record = getWeekdays(locale);
  const keys = Object.keys(record).sort() as unknown as WeekdaysEnum[];
  return keys.map(key => record[key]);
}

function init(
  props: InitialProperties,
  _cmd: Cmd,
  model?: Model
): ImmutableResult<Model> {
  const locale = <string>props.get('locale') ?? 'en';
  const weekDays = model?.days.all ?? getSelectorWeekdays(locale);
  const weekendDays = model?.days.weekend ?? getSelectorWeekendDays(locale);
  const onlyWorkdays = <boolean>props.get('onlyWorkdays') === true;
  const multiSelection = <boolean>props.get('multiSelect') || false;
  const valueAsArray = props.get('value')
    ? validateWeekdays(<unknown[]>props.get('value'))
    : [];
  const value =
    model?.value && model.value.size > 0 ? model.value : new Set(valueAsArray);
  const disabled = <boolean>props.get('disabled') ?? false;

  return {
    locale,
    days: {
      all: weekDays,
      weekend: weekendDays,
    },
    value,
    valueAsArray: Array.from(value),
    onlyWorkdays,
    multiSelection,
    disabled,
  };
}

// -- Update
function emitChangeEvent(
  host: WeekdaySelector,
  changeEvent: {action: 'select' | 'deselect'; value: Weekday}
) {
  host.dispatchEvent(
    ChangeWeekdayEvent.create({
      values: host.model.valueAsArray,
      change: changeEvent,
    })
  );
  host.updateFormValue();
}

function update(msg: Msg, model: Model, cmd: Cmd): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'set-locale': {
      const supportedLocale = locale.getSupportedLocaleForValue(msg.value);
      if (supportedLocale === model.locale) return model;
      return {
        ...model,
        locale: supportedLocale,
        days: {
          all: getSelectorWeekdays(supportedLocale),
          weekend: getSelectorWeekendDays(supportedLocale),
        },
      };
    }

    case 'set-only-workdays':
      return {
        ...model,
        onlyWorkdays: msg.value,
      };

    case 'allow-multi-selection':
      return {
        ...model,
        multiSelection: msg.value,
      };

    case 'set-value': {
      const value = validateWeekdays(msg.value).sort();
      return {
        ...model,
        value: new Set(value),
        valueAsArray: value,
      };
    }

    case 'set-single-value': {
      const value = new Set([msg.value]);
      return [
        {
          ...model,
          value,
          valueAsArray: Array.from(value).sort(),
        },
        cmd((host: WeekdaySelector) => {
          emitChangeEvent(host, {action: 'select', value: msg.value});
        }),
      ];
    }

    case 'select-weekday': {
      const value = new Set([...model.valueAsArray, msg.value]);
      return [
        {
          ...model,
          value,
          valueAsArray: Array.from(value).sort(),
        },
        cmd((host: WeekdaySelector) => {
          emitChangeEvent(host, {action: 'select', value: msg.value});
        }),
      ];
    }

    case 'deselect-weekday': {
      model.value.delete(msg.value);
      const value = new Set(model.value);
      return [
        {
          ...model,
          value,
          valueAsArray: Array.from(value).sort(),
        },
        cmd((host: WeekdaySelector) => {
          emitChangeEvent(host, {action: 'deselect', value: msg.value});
        }),
      ];
    }

    case 'enable': {
      return {
        ...model,
        disabled: false,
      };
    }

    case 'disable': {
      return {
        ...model,
        disabled: true,
      };
    }
  }
}

// -- Program

export const main: ImmutableProgram<Model, Msg> = {
  init,
  update,
};
